import React, { Component } from 'react';
import {
  Card,
  Col,
  Descriptions,
  Row,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import moment from 'moment';
import axios from 'util/Api';

class SpareStockTakingAdjustmentDetails extends Component {
  state = {
    loading: false,
    details: null,
  };

  componentDidMount() {
    this.loadDetails();
  }

  formatDate = (value) => {
    if (!value) {
      return '-';
    }
    return moment(value).format('DD/MM/YYYY HH:mm:ss');
  };

  formatUser = (user) => {
    if (!user) {
      return '-';
    }
    const name = user.name || '-';
    return user.email ? `${name} (${user.email})` : name;
  };

  loadDetails = async () => {
    const voucherParam = this.props.match
      && this.props.match.params
      && this.props.match.params.voucherNo
      ? this.props.match.params.voucherNo
      : '';
    const voucherNo = decodeURIComponent(voucherParam || '').trim();

    if (!voucherNo) {
      message.error('Invalid adjustment voucher number');
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await axios.get(`/spare-stock-taking/adjustment-details/${encodeURIComponent(voucherNo)}`);
      this.setState({ details: response && response.data ? response.data : null });
    } catch (error) {
      message.error(
        (error && error.response && error.response.data && error.response.data.message)
        || 'Failed to load adjustment details',
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  renderStatusTag = (status) => {
    if (status === 'CLOSED') {
      return <Tag color="green">{status}</Tag>;
    }
    if (status === 'SUBMITTED') {
      return <Tag color="blue">{status}</Tag>;
    }
    return <Tag color="orange">{status || 'DRAFT'}</Tag>;
  };

  renderActionTag = (action) => {
    if (action === 'ADJUSTMENT') {
      return <Tag color="blue">ADJUSTMENT</Tag>;
    }
    if (action === 'IGNORE') {
      return <Tag>IGNORE</Tag>;
    }
    return <Tag color="orange">PENDING</Tag>;
  };

  render() {
    const { loading, details } = this.state;
    const sav = details && details.sav ? details.sav : null;
    const sst = details && details.sst ? details.sst : null;
    const record = details && details.record ? details.record : null;

    const recordColumns = [
      {
        title: 'Spare Part',
        dataIndex: 'spareName',
        key: 'spareName',
      },
      {
        title: 'Spare Type',
        dataIndex: 'spareTypeName',
        key: 'spareTypeName',
        render: (value) => value || '-',
      },
      {
        title: 'Location',
        dataIndex: 'locationName',
        key: 'locationName',
      },
      {
        title: 'Sub Location',
        dataIndex: 'subLocationName',
        key: 'subLocationName',
        render: (value) => value || '-',
      },
      {
        title: 'System Balance',
        dataIndex: 'systemBalance',
        key: 'systemBalance',
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        render: (value) => this.renderActionTag(value),
      },
      {
        title: 'Adjustment Qty',
        dataIndex: 'adjustmentQuantity',
        key: 'adjustmentQuantity',
      },
      {
        title: 'Rate',
        dataIndex: 'adjustmentRate',
        key: 'adjustmentRate',
      },
      {
        title: 'Processed By',
        dataIndex: 'processedBy',
        key: 'processedBy',
        render: (value) => this.formatUser(value),
      },
      {
        title: 'Processed At',
        dataIndex: 'processedAt',
        key: 'processedAt',
        render: (value) => this.formatDate(value),
      },
      {
        title: 'Remarks',
        dataIndex: 'remarks',
        key: 'remarks',
        render: (value) => value || '-',
      },
    ];

    const recordData = record
      ? [{ ...record, key: record._id || 'record' }]
      : [];

    return (
      <Spin spinning={loading}>
        <Card className="gx-card-metrics" title={`Spare Adjustment Details${sav && sav.voucherNo ? ` - ${sav.voucherNo}` : ''}`}>
          {!sav && (
            <div>No adjustment details found.</div>
          )}

          {sav && (
            <Row gutter={16}>
              <Col span={24}>
                <Descriptions bordered size="small" column={2} title="SAV Details">
                  <Descriptions.Item label="Voucher No">{sav.voucherNo || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Transaction Type">{sav.transactionType || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Adjustment Qty">{sav.adjustmentQuantity}</Descriptions.Item>
                  <Descriptions.Item label="Rate">{sav.rate}</Descriptions.Item>
                  <Descriptions.Item label="Value">{sav.value}</Descriptions.Item>
                  <Descriptions.Item label="Created At">{this.formatDate(sav.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="Created By">{this.formatUser(sav.createdBy)}</Descriptions.Item>
                  <Descriptions.Item label="Stock Taking Date">{this.formatDate(sav.stockTakingDate)}</Descriptions.Item>
                  <Descriptions.Item label="Spare Part">{`${(sav.spare && sav.spare.name) || '-'}${(sav.spare && sav.spare.code) ? ` (${sav.spare.code})` : ''}`}</Descriptions.Item>
                  <Descriptions.Item label="Location">{(sav.location && sav.location.name) || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Sub Location">{(sav.subLocation && sav.subLocation.name) || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Remarks">{sav.remarks || '-'}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          )}

          {sst && (
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Descriptions bordered size="small" column={2} title="SST Details">
                  <Descriptions.Item label="SST Voucher">{sst.voucherNo || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Status">{this.renderStatusTag(sst.status)}</Descriptions.Item>
                  <Descriptions.Item label="Stock Taking Date">{this.formatDate(sst.stockTakingDate)}</Descriptions.Item>
                  <Descriptions.Item label="Generated At">{this.formatDate(sst.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="Generated By">{this.formatUser(sst.generatedBy)}</Descriptions.Item>
                  <Descriptions.Item label="Closed By">{this.formatUser(sst.completedBy)}</Descriptions.Item>
                  <Descriptions.Item label="Closed At">{this.formatDate(sst.completedAt)}</Descriptions.Item>
                  <Descriptions.Item label="Pending Records">{`${sst.pendingRecords}/${sst.totalRecords}`}</Descriptions.Item>
                  <Descriptions.Item label="Filter Location">{(sst.filters && sst.filters.location && sst.filters.location.name) || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Filter Sub Location">{(sst.filters && sst.filters.subLocation && sst.filters.subLocation.name) || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Filter Spare Type">{(sst.filters && sst.filters.spareType && sst.filters.spareType.name) || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Filter Spare Part">{(sst.filters && sst.filters.sparePart && sst.filters.sparePart.name) || '-'}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          )}

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Table
                bordered
                size="small"
                pagination={false}
                columns={recordColumns}
                dataSource={recordData}
                scroll={{ x: true }}
                title={() => 'SST Record Details'}
              />
            </Col>
          </Row>
        </Card>
      </Spin>
    );
  }
}

export default SpareStockTakingAdjustmentDetails;
